import { tradeDisplayLog } from '../debug/browser-logging'
import { notifyError } from '../notifications'

const READONLY_TRADING_NOTIFICATION_TITLE = 'Readonly tournament chart'
const READONLY_TRADING_NOTIFICATION_DETAIL =
  'Tournament trading is backend-controlled. Chart-side placement and edits are disabled.'
const READONLY_TRADING_NOTIFICATION_THROTTLE_MS = 1500

let lastReadonlyNotificationKey = ''
let lastReadonlyNotificationAt = 0

export interface ReadonlyTradingSettingsSnapshot {
  visible: boolean
  showOrders: boolean
  showPositions: boolean
  executedOrdersEnabled: boolean
}

export const readReadonlyTradingSettings = (
  chartSettings: any
): ReadonlyTradingSettingsSnapshot => ({
  visible: chartSettings?.chartReact?.trading?.visible !== false,
  showOrders: chartSettings?.chartReact?.trading?.showOrders !== false,
  showPositions: chartSettings?.chartReact?.trading?.showPositions !== false,
  executedOrdersEnabled: chartSettings?.chartReact?.trading?.executedOrders?.enabled !== false,
})

export const applyReadonlyTradingSettings = (
  chartSettings: any
): {
  changed: boolean
  nextSettings: any
  current: ReadonlyTradingSettingsSnapshot
  required: ReadonlyTradingSettingsSnapshot
} => {
  const current = readReadonlyTradingSettings(chartSettings)
  const required: ReadonlyTradingSettingsSnapshot = {
    visible: true,
    showOrders: true,
    showPositions: true,
    executedOrdersEnabled: true,
  }
  const changed =
    current.visible !== required.visible ||
    current.showOrders !== required.showOrders ||
    current.showPositions !== required.showPositions ||
    current.executedOrdersEnabled !== required.executedOrdersEnabled

  if (!changed) {
    return {
      changed: false,
      nextSettings: chartSettings,
      current,
      required,
    }
  }

  return {
    changed: true,
    nextSettings: {
      ...chartSettings,
      chartReact: {
        ...(chartSettings?.chartReact ?? {}),
        trading: {
          ...(chartSettings?.chartReact?.trading ?? {}),
          visible: true,
          showOrders: true,
          showPositions: true,
          executedOrders: {
            ...(chartSettings?.chartReact?.trading?.executedOrders ?? {}),
            enabled: true,
          },
        },
      },
    },
    current,
    required,
  }
}

const maybeNotifyReadonlyTrading = (action: string): void => {
  const now = Date.now()
  if (
    lastReadonlyNotificationKey === action &&
    now - lastReadonlyNotificationAt < READONLY_TRADING_NOTIFICATION_THROTTLE_MS
  ) {
    return
  }

  lastReadonlyNotificationKey = action
  lastReadonlyNotificationAt = now
  notifyError(READONLY_TRADING_NOTIFICATION_TITLE, READONLY_TRADING_NOTIFICATION_DETAIL)
}

export const blockReadonlyTradingMutation = <T>(
  action: string,
  fallback: T,
  details?: Record<string, unknown>
): T => {
  tradeDisplayLog('readonly-trading blocked mutation', {
    action,
    ...(details ?? {}),
  })
  console.warn('[ReadonlyTrading] Blocked chart mutation', {
    action,
    ...(details ?? {}),
  })
  maybeNotifyReadonlyTrading(action)
  return fallback
}
